import React, { Component } from "react";
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { Typography, FormControl, Select, MenuItem, Switch, FormControlLabel, Table, TableHead, TableRow, TableCell, TableBody, InputLabel, Button, Grid } from "@material-ui/core";
import TextField from '@material-ui/core/TextField';
import withRoot from "../withRoot";

const styles = theme => ({
    root: {
        margin: theme.spacing.unit,
    },
    margin: {
        margin: theme.spacing.unit,
    },
    formControl: {
        margin: theme.spacing.unit,
        minWidth: 40,
    },
    table: {
        width: "auto",
    },
});

const feetPerMile = 5280;
const maxHoursPerDay = 24;
const minutesPerHour = 60;
const minutesPerDay = maxHoursPerDay * minutesPerHour;
const travelHoursPerDay = 8;
const travelMinutesPerDay = travelHoursPerDay * minutesPerHour;

class TravelPlanner extends Component {
    state = {
        travelDistance: 90,
        travelDistanceWidth: 35,
        travelSpeed: 30,
        travelSpeedWidth: 35,
        speedUnit: "ft",
        waterborne: false,
        pace: 1,
        difficultTerrain: false,
        difficultMove: false,
        gallop: false,
        gallopMinutes: 0,
        highAltitude: false,
        dailyTravelMinutes: 0,
        dailyTimeMinutes: 0,
        march: false,
        marchMinutes: 0,
        marchHour: 0,
        travelTime: 1,
        travelTimeWidth: 26,
        travelLog: [{}],
        travelIndex: 0,
    };

    handleNumberChange = name => event => {
        let value = Number(event.target.value);
        let length = event.target.value.length;
        if (value < 1) {
            length = value = 1;
        }

        this.setState({
            [name]: value,
            [name + "Width"]: length * 9 + 17,
        });
    }

    handleValueChange = name => (event) => {
        const value = event.target.value;
        const stateUpdate = { [name]: value };
        if (name === 'pace' && value !== 2) {
            stateUpdate.gallop = false;
        }
        this.setState(stateUpdate);
    }

    handleBoolChange = name => () => {
        const stateUpdate = { [name]: !this.state[name] };
        if (stateUpdate[name]) {
            if (name === 'gallop') {
                stateUpdate.pace = 2;
                stateUpdate.waterborne = false;
            } else if (name === 'waterborne') {
                stateUpdate.gallop = false;
            }
        }
        this.setState(stateUpdate);
    }

    getDailyTravelMinsLeft() {
        const dayMinutesLeft = minutesPerDay - this.state.dailyTimeMinutes;

        if (this.state.waterborne) {
            return dayMinutesLeft;
        }

        let travelMinutesLeft = travelMinutesPerDay - this.state.dailyTravelMinutes;
        if (this.state.march) {
            travelMinutesLeft = minutesPerHour - this.state.marchMinutes;
        }

        travelMinutesLeft = Math.min(travelMinutesLeft, dayMinutesLeft);

        if (this.state.gallop) {
            const gallopMinutesLeft = minutesPerHour - this.state.gallopMinutes;
            travelMinutesLeft = Math.min(travelMinutesLeft, gallopMinutesLeft);
        }

        if (this.state.highAltitude) {
            travelMinutesLeft /= 2;
        }

        return travelMinutesLeft;
    }

    getFormateDistance(feet) {
        let distanceText = "";

        const miles = Math.floor(feet / feetPerMile);
        if (miles > 0) {
            distanceText += miles + " mile" + (miles > 1 ? "s" : "");
        }

        feet -= miles * feetPerMile;
        if (feet > 0) {
            distanceText += (distanceText.length > 0 ? ", " : "") + feet + (feet > 1 ? " feet" : " foot");
        }

        if (distanceText.length === 0) {
            distanceText = "0 miles";
        }

        return distanceText;
    }

    getFormatedTimeText(minutes) {
        let durationText = "";

        minutes = Math.ceil(minutes);
        const hoursLeft = Math.floor(minutes / minutesPerHour);
        if (hoursLeft > 0) {
            durationText += hoursLeft + " hour" + (hoursLeft > 1 ? "s" : "");
        }

        minutes -= hoursLeft * minutesPerHour;
        if (minutes > 0) {
            durationText += (durationText.length > 0 ? ", " : "") + minutes + " minute" + (minutes > 1 ? "s" : "");
        }

        return durationText;
    }

    getEstimatedDurationText(minutes) {
        const hoursPerDay = this.state.waterborne ? maxHoursPerDay : travelHoursPerDay;
        let durationText = "";

        let daysLeft = Math.floor(minutes / minutesPerHour / hoursPerDay);
        let minutesLeft = minutes - daysLeft * minutesPerHour * hoursPerDay;

        if (minutesLeft === 0 && hoursPerDay !== maxHoursPerDay) {
            daysLeft -= 1;
            minutesLeft += hoursPerDay * minutesPerHour;
        }

        if (daysLeft > 0) {
            durationText += daysLeft + " day" + (daysLeft > 1 ? "s" : "");
        }

        const hoursAndMinutes = this.getFormatedTimeText(minutesLeft);
        if (hoursAndMinutes.length > 0) {
            if (durationText.length > 0) {
                durationText += ", " + hoursAndMinutes;
            }
        }

        return durationText;
    }

    logTravel(minutes) {
        const travelLog = this.state.travelLog.concat();
        const travelDay = travelLog[this.state.travelIndex];

        let speedMph = this.state.speedUnit === "ft" ? Number(this.state.travelSpeed) / 10 : this.state.travelSpeed;

        let speedDifficulty = 1;
        if (this.state.difficultTerrain) {
            speedDifficulty += 1;
        }
        if (this.state.difficultMove) {
            speedDifficulty += 1;
        }

        let speedMultiplier = 1;
        if (this.state.gallop) {
            speedMultiplier += 1;
        }

        speedMph *= speedMultiplier / speedDifficulty;
        let speedFeetPerMinute = speedMph * feetPerMile / minutesPerHour;
        if (this.state.pace === 0) {
            speedFeetPerMinute *= 2 / 3;
        } else if (this.state.pace === 2) {
            speedFeetPerMinute *= 4 / 3;
        }
        const distance = speedFeetPerMinute * minutes;

        travelDay.totalDistance = travelDay.totalDistance ? travelDay.totalDistance + distance : distance;
        travelDay.totalTravel = travelDay.totalTravel ? travelDay.totalTravel + minutes : minutes;

        this.setState({
            travelLog: travelLog,
        });
    }

    dailyTravel(minutes) {
        const stateUpdate = {
            dailyTimeMinutes: this.state.dailyTimeMinutes + minutes,
        };

        if (this.state.highAltitude) {
            minutes *= 2;
        }

        if (!this.state.waterborne) {
            stateUpdate.dailyTravelMinutes = this.state.dailyTravelMinutes + minutes;
        }

        if (this.state.gallop) {
            stateUpdate.gallopMinutes = this.state.gallopMinutes + minutes;

            if (minutesPerHour - stateUpdate.gallopMinutes === 0) {
                stateUpdate.gallop = false;
            }
        }

        if (stateUpdate.dailyTimeMinutes >= travelMinutesPerDay) {
            if (!this.state.march) {
                stateUpdate.march = true;
                stateUpdate.marchHour = 0;
                stateUpdate.marchMinutes = 0;
            } else {
                stateUpdate.marchMinutes = this.state.marchMinutes + minutes;
                stateUpdate.marchHour = this.state.marchHour + Math.floor(stateUpdate.marchMinutes / minutesPerHour);
                stateUpdate.marchMinutes = stateUpdate.marchMinutes % minutesPerHour;
            }
        }

        this.setState(stateUpdate);
    }

    travelMinutes() {
        const traveledMinutes = Math.min(this.state.travelTime, this.getDailyTravelMinsLeft());

        this.dailyTravel(traveledMinutes);
        this.logTravel(traveledMinutes);
    }

    travelHours() {
        const traveledMinutes = Math.min(this.state.travelTime * minutesPerHour, this.getDailyTravelMinsLeft());

        this.dailyTravel(traveledMinutes);
        this.logTravel(traveledMinutes);
    }

    travelDay() {
        const traveledMinutes = this.getDailyTravelMinsLeft();

        this.dailyTravel(traveledMinutes);
        this.logTravel(traveledMinutes);
    }

    refreshMount() {
        this.setState({ gallopMinutes: 0 });
    }

    nextDay() {
        const travelLog = this.state.travelLog.concat();
        travelLog[travelLog.length] = {};

        this.refreshMount();
        this.setState({
            dailyTravelMinutes: 0,
            dailyTimeMinutes: 0,
            marchMinutes: 0,
            marchHour: 0,
            march: false,
            travelLog: travelLog,
            travelIndex: this.state.travelIndex + 1,
        });
    }

    reset() {
        this.nextDay();
        this.setState({
            travelLog: [{}],
            travelIndex: 0,
        });
    }

    render() {
        const { classes } = this.props;
        const {
            waterborne,
            travelTime,
        } = this.state;

        const speedMph = this.state.speedUnit === "ft" ? Number(this.state.travelSpeed) / 10 : this.state.travelSpeed;
        const speedFeetPerMinute = speedMph * feetPerMile / minutesPerHour;
        const distanceFeet = this.state.travelDistance * feetPerMile;

        const estimatedSlowDuration = this.getEstimatedDurationText(distanceFeet / (speedFeetPerMinute * 2 / 3));
        const estimatedNormalDuration = this.getEstimatedDurationText(distanceFeet / speedFeetPerMinute);
        const estimatedFastDuration = this.getEstimatedDurationText(distanceFeet / (speedFeetPerMinute * 4 / 3));

        let dailyTimeLeft = "";
        const totalTravelMinsLeft = this.getDailyTravelMinsLeft();
        const hoursLeft = Math.floor(totalTravelMinsLeft / minutesPerHour);
        if (hoursLeft > 0) {
            dailyTimeLeft += hoursLeft + " hour" + (hoursLeft === 1 ? "" : "s");
        }
        const minsLeft = totalTravelMinsLeft - Math.floor(totalTravelMinsLeft / minutesPerHour) * minutesPerHour;
        if (minsLeft > 0) {
            dailyTimeLeft += (dailyTimeLeft.length > 0 ? ", " : "") + minsLeft + " minute" + (minsLeft === 1 ? "" : "s");
        }
        if (dailyTimeLeft.length === 0) {
            dailyTimeLeft = "None";
        }

        let pacePenalties = "";
        if (this.state.pace === 1) {
            pacePenalties = "No stealth";
        } else if (this.state.pace === 2) {
            pacePenalties = "No stealth, -5 to passive Wisdom (Perception)";
        }

        const travelHours = Math.min(travelTime, hoursLeft);
        const showTravelHours = travelTime < hoursLeft || (travelTime === hoursLeft && minsLeft > 0);

        const travelMins = Math.min(travelTime, totalTravelMinsLeft);
        const showTravelMins = travelTime < totalTravelMinsLeft;

        const travelButtons = this.state.march && !waterborne ? (
            <div>
                <TextField
                    id="travelTime"
                    value={this.state.travelTime}
                    onChange={this.handleNumberChange('travelTime')}
                    type="number"
                    style={{ width: this.state.travelTimeWidth }}
                    className={classes.margin}
                />

                {showTravelMins ? (<Button variant="raised" color="secondary" className={classes.margin} onClick={() => this.travelMinutes()}>Travel {travelMins} minute{travelMins === 1 ? null : "s"}</Button>) : null}
                <Button variant="raised" color="secondary" className={classes.margin} onClick={() => this.travelDay()}>Travel {dailyTimeLeft}</Button>
            </div>
        ) : (
                <div>
                    <TextField
                        id="travelTime"
                        value={this.state.travelTime}
                        onChange={this.handleNumberChange('travelTime')}
                        type="number"
                        style={{ width: this.state.travelTimeWidth }}
                        className={classes.margin}
                    />

                    {showTravelMins ? (<Button variant="raised" color="primary" className={classes.margin} onClick={() => this.travelMinutes()}>Travel {travelMins} minute{travelMins === 1 ? null : "s"}</Button>) : null}
                    {showTravelHours ? (<Button variant="raised" color="primary" className={classes.margin} onClick={() => this.travelHours()}>Travel {travelHours} hour{travelHours === 1 ? null : "s"}</Button>) : null}
                    <Button variant="raised" color="primary" className={classes.margin} onClick={() => this.travelDay()}>Travel {dailyTimeLeft}</Button>
                </div>
            );

        let travelDistanceLeft = distanceFeet;
        const travelLog = this.state.travelLog.map((value, index) => (
            <TableRow key={index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{value.totalTravel ? this.getFormatedTimeText(value.totalTravel) : "0 minutes"}</TableCell>
                <TableCell>{this.getFormateDistance(value.totalDistance ? value.totalDistance : 0)}</TableCell>
                <TableCell>{this.getFormateDistance(travelDistanceLeft -= (value.totalDistance ? value.totalDistance : 0))}</TableCell>
            </TableRow>
        ));

        return (
            <Grid container className={classes.root}>
                <Grid className={classes.margin} item>
                    <Typography component="div" className={classes.root}>
                        Travel Distance:
                    <TextField
                            id="travelDistance"
                            value={this.state.travelDistance}
                            onChange={this.handleNumberChange('travelDistance')}
                            type="number"
                            style={{ width: this.state.travelDistanceWidth }}
                            className={classes.margin}
                        />
                        miles
                    </Typography>

                    <Typography component="div" className={classes.root}>
                        Travel Speed:
                    <TextField
                            id="travelSpeed"
                            value={this.state.travelSpeed}
                            onChange={this.handleNumberChange('travelSpeed')}
                            type="number"
                            style={{ width: this.state.travelSpeedWidth }}
                            className={classes.margin}
                        />

                        <FormControl className={classes.speedUnit}>
                            <Select value={this.state.speedUnit} onChange={this.handleValueChange("speedUnit")}>
                                {["ft", "mph"].map((value) => (
                                    <MenuItem key={value} value={value}>{value}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Typography>

                    <div>
                        <FormControlLabel label="Waterborne"
                            control={
                                <Switch
                                    checked={waterborne}
                                    onChange={this.handleBoolChange('waterborne')}
                                    value="waterborne"
                                    color="primary"
                                />
                            }
                        />
                    </div>

                    <Typography>Estimated Duration</Typography>
                    <Table className={classes.table}>
                        <TableHead>
                            <TableRow>
                                {!waterborne ? (<TableCell>Slow</TableCell>) : null}
                                <TableCell>Normal</TableCell>
                                {!waterborne ? (<TableCell>Fast</TableCell>) : null}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                {!waterborne ? (<TableCell>{estimatedSlowDuration}</TableCell>) : null}
                                <TableCell>{estimatedNormalDuration}</TableCell>
                                {!waterborne ? (<TableCell>{estimatedFastDuration}</TableCell>) : null}
                            </TableRow>
                        </TableBody>
                    </Table>

                    <Typography variant="caption">
                        <FormControl className={classes.formControl}>
                            <InputLabel htmlFor="pace">Pace</InputLabel>
                            <Select value={this.state.pace} onChange={this.handleValueChange("pace")} inputProps={{ id: 'pace' }}>
                                {["Slow", "Normal", "Fast"].map((value, index) => (
                                    <MenuItem key={index} value={index}>{value}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {pacePenalties}
                    </Typography>

                    <div>
                        <FormControlLabel label="Difficult Terrain"
                            control={
                                <Switch
                                    checked={this.state.difficultTerrain}
                                    onChange={this.handleBoolChange('difficultTerrain')}
                                    value="difficultTerrain"
                                    color="primary"
                                />
                            }
                        />
                    </div>

                    <div>
                        <FormControlLabel label="Climbing, Swimming, or Crawling"
                            control={
                                <Switch
                                    checked={this.state.difficultMove}
                                    onChange={this.handleBoolChange('difficultMove')}
                                    value="difficultMove"
                                    color="primary"
                                />
                            }
                        />
                    </div>

                    <Typography variant="caption">
                        <FormControlLabel label="Gallop"
                            control={
                                <Switch
                                    checked={this.state.gallop}
                                    onChange={this.handleBoolChange('gallop')}
                                    value="gallop"
                                    color="primary"
                                    disabled={minutesPerHour - this.state.gallopMinutes === 0}
                                />
                            }
                        />

                        Time Left: {minutesPerHour - this.state.gallopMinutes} minute{this.state.gallopMinutes === 59 ? null : "s"}
                        <Button variant="raised" className={classes.margin} onClick={() => this.refreshMount()}>Refresh Mount</Button>
                    </Typography>

                    <div>
                        <FormControlLabel label="High Altitude"
                            control={
                                <Switch
                                    checked={this.state.highAltitude}
                                    onChange={this.handleBoolChange('highAltitude')}
                                    value="highAltitude"
                                    color="primary"
                                />
                            }
                        />
                    </div>

                </Grid>
                <Grid className={classes.margin} item>

                    <Typography className={classes.margin}>Travel Time Left: {dailyTimeLeft}</Typography>

                    {this.state.march && !waterborne && dailyTimeLeft !== "None" ? (<Typography variant="caption">Forced March - Save: DC {11 + this.state.marchHour} Constitution</Typography>) : null}

                    {dailyTimeLeft !== "None" ? travelButtons : null }

                    <Typography>Travel Log</Typography>
                    <Table className={classes.table}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Day</TableCell>
                                <TableCell>Travel Time</TableCell>
                                <TableCell>Travel Distance</TableCell>
                                <TableCell>Distance Left</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {travelLog}
                        </TableBody>
                    </Table>

                    <div>
                        <Button variant="raised" className={classes.margin} onClick={() => this.nextDay()}>Next Day</Button>
                        <Button variant="raised" className={classes.margin} onClick={() => this.reset()}>Reset Travel</Button>
                    </div>
                </Grid>
            </Grid>
        );
    }
}

TravelPlanner.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withRoot("Travel Planner", withStyles(styles)(TravelPlanner));


// WEBPACK FOOTER //
// ./src/travel-planner/TravelPlanner.js